import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  adfToEditor,
  editorToAdfOrEmpty,
  type AdfDocument,
  type EditorDocument,
} from '@jisy/shared';
import styles from './AdfEditor.module.css';

/**
 * Редактор описания задачи.
 *
 * Работает с разметкой, а не с деревом ADF напрямую: подмножество узлов разбирается в текст,
 * остальное откладывается и возвращается в Jira нетронутым. Метка ⟦N⟧ в тексте показывает,
 * где стоит неразобранный блок, — стереть его можно, но только намеренно.
 *
 * Макета для редактора нет: панель и поле собраны из тех же токенов, что и остальные поля формы.
 */

interface ToolbarAction {
  key: string;
  label: string;
  wrap: [string, string];
  block?: boolean;
}

const ACTIONS: ToolbarAction[] = [
  { key: 'bold', label: '𝐁', wrap: ['**', '**'] },
  { key: 'italic', label: '𝐼', wrap: ['_', '_'] },
  { key: 'strike', label: 'S̶', wrap: ['~~', '~~'] },
  { key: 'code', label: '‹›', wrap: ['`', '`'] },
  { key: 'link', label: '⛓', wrap: ['[', '](https://)'] },
  { key: 'list', label: '•—', wrap: ['- ', ''], block: true },
  { key: 'heading', label: 'H', wrap: ['## ', ''], block: true },
  { key: 'codeBlock', label: '{ }', wrap: ['```\n', '\n```'], block: true },
];

export function AdfEditor({
  value,
  onChange,
  placeholder,
  minRows = 6,
}: {
  value: EditorDocument;
  onChange: (next: EditorDocument) => void;
  placeholder?: string;
  minRows?: number;
}): React.ReactElement {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const apply = useCallback(
    (action: ToolbarAction) => {
      const field = textareaRef.current;
      if (!field) return;

      const start = field.selectionStart;
      const end = field.selectionEnd;
      const selected = value.text.slice(start, end);
      const [before, after] = action.wrap;

      // Блочные действия ставятся с начала строки, иначе разметка не сработает.
      const insertion = action.block
        ? `${start > 0 && value.text[start - 1] !== '\n' ? '\n' : ''}${before}${selected}${after}`
        : `${before}${selected}${after}`;

      const next = value.text.slice(0, start) + insertion + value.text.slice(end);
      onChange({ ...value, text: next });

      // Возвращаем курсор внутрь вставленной разметки, чтобы можно было сразу печатать.
      requestAnimationFrame(() => {
        const position = start + insertion.length - after.length;
        field.focus();
        field.setSelectionRange(position, position);
      });
    },
    [onChange, value],
  );

  const preservedCount = value.preserved.length;

  return (
    <div className={styles.editor}>
      <div className={styles.toolbar} role="toolbar" aria-label={t('issue.description')}>
        {ACTIONS.map((action) => (
          <button
            type="button"
            key={action.key}
            className={styles.toolButton}
            onClick={() => apply(action)}
            title={t(`editor.${action.key}`)}
            aria-label={t(`editor.${action.key}`)}
          >
            {action.label}
          </button>
        ))}
      </div>

      <textarea
        ref={textareaRef}
        className={styles.textarea}
        rows={minRows}
        value={value.text}
        onChange={(event) => onChange({ ...value, text: event.target.value })}
        placeholder={placeholder ?? ''}
        spellCheck
      />

      {preservedCount > 0 && (
        <p className={styles.preservedHint}>{t('editor.preserved', { count: preservedCount })}</p>
      )}
    </div>
  );
}

/** Готовит документ Jira к редактированию и обратно. */
export function useAdfEditor(description: AdfDocument | null): {
  document: EditorDocument;
  setDocument: (next: EditorDocument) => void;
  toAdf: () => AdfDocument;
  reset: () => void;
  dirty: boolean;
} {
  const initial = useMemo(() => adfToEditor(description), [description]);
  const [document, setDocument] = useState<EditorDocument>(initial);
  const [baseline, setBaseline] = useState(initial);

  // Пришло новое описание с сервера — начинаем от него, но не затираем несохранённую правку.
  if (baseline !== initial && document.text === baseline.text) {
    setBaseline(initial);
    setDocument(initial);
  }

  return {
    document,
    setDocument,
    toAdf: () => editorToAdfOrEmpty(document),
    reset: () => setDocument(baseline),
    dirty: document.text !== baseline.text,
  };
}
